import { createContext, type FC, type PropsWithChildren, useContext, useMemo, useState } from 'react'
import { Box } from '@dhl-ui/components'

type ContextValue = {
  height: number
  setHeight: (height: number) => void
}

const FixedBottomBoxContext = createContext<ContextValue>({
  height: 0,
  setHeight: () => {},
})

/**
 * Wraps a region that may contain a `FixedBottomBox`. The provider tracks the
 * height of the currently rendered bar so that consumers (e.g. the page scroll
 * container) can reserve room for it without having to apply padding to the
 * bar's previous DOM sibling.
 */
export const FixedBottomBoxHeightProvider: FC<PropsWithChildren> = ({ children }) => {
  const [height, setHeight] = useState(0)
  const value = useMemo<ContextValue>(() => ({ height, setHeight }), [height])
  return <FixedBottomBoxContext.Provider value={value}>{children}</FixedBottomBoxContext.Provider>
}

/**
 * Internal hook used by `FixedBottomBox` to publish its measured height.
 */
export const useFixedBottomBoxHeightSetter = (): ContextValue['setHeight'] =>
  useContext(FixedBottomBoxContext).setHeight

/**
 * Renders an invisible spacer that reserves vertical room equal to the height
 * of the currently mounted `FixedBottomBox`. Place this inside the scroll
 * container so the last bit of content is not hidden behind the fixed bar.
 */
export const FixedBottomBoxSpacer: FC = () => {
  const { height } = useContext(FixedBottomBoxContext)
  return <Box aria-hidden sx={{ height: `${height}px`, flexShrink: 0, mt: height && height > 0 ? 3 : 0 }} />
}
